import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Save, Shield, Lock, Mail, Info, Plus, X, Crown } from 'lucide-react'
import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { AddResourcePermissionDialog, type ScopedGrantInput } from '@/components/permissions/AddResourcePermissionDialog'
import { DuplicateOverrideDialog } from '@/components/permissions/DuplicateOverrideDialog'
import { PermissionTabs } from '@/components/permissions/PermissionTabs'
import {
  indexCatalog,
  instanceGrantableActions,
  useResourceNameResolver,
  type ScopedGrantRow,
} from '@/components/permissions/permissionCatalog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import {
  usePermission,
  usePermissionCatalog,
  invalidatePermissionQueries,
  permissionQueryKeys,
} from '@/hooks/usePermission'
import { useRecordGroups } from '@/hooks/useRecordGroups'
import {
  getEffectivePermissions,
  replaceUserPermissions,
  grantUserPermission,
  revokeUserGrant,
  getRoles,
  asPermissionAlreadyInherited,
} from '@/services/api/permission.service'
import { getWorkspaceUsers, assignRole, removeRole } from '@/services/api/workspace.service'
import { useAuthStore } from '@/stores/auth.store'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import { useSubscriptionStore } from '@/stores/subscription.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { PermissionAlreadyInherited, PermissionSource } from '@/types/api/permission'
import type { ReplacePermissionsRequest } from '@/types/api/role'
import { RoleType } from '@/types/enums'


/** An action that hit "already granted through a role" and awaits confirmation. */
type PendingDuplicate = { conflict: PermissionAlreadyInherited; retry: () => Promise<void> }

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId)
  const currentUserId = useAuthStore((s) => s.userId)
  const { lockedResourceIds } = useSubscriptionStore()
  const { can, canAnywhere, isOwner, isAdmin } = usePermission()

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['workspace-users'],
    queryFn: getWorkspaceUsers,
  })
  const user = users.find((u) => u.userId === userId)

  const { data: effective, isLoading: effectiveLoading } = useQuery({
    queryKey: permissionQueryKeys.effective(workspaceId, userId ?? ''),
    queryFn: () => getEffectivePermissions(userId!),
    enabled: !!userId,
  })
  const { data: allRoles = [] } = useQuery({ queryKey: ['roles'], queryFn: getRoles })
  const { data: catalog } = usePermissionCatalog()
  const index = useMemo(() => (catalog ? indexCatalog(catalog) : null), [catalog])
  const { recordGroups = [] } = useRecordGroups()

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [hasChanges, setHasChanges] = useState(false)
  const [addCategory, setAddCategory] = useState<string | null>(null)
  const [pendingDuplicate, setPendingDuplicate] = useState<PendingDuplicate | null>(null)
  const [confirmingDuplicate, setConfirmingDuplicate] = useState(false)
  const [roleToAdd, setRoleToAdd] = useState('')
  const [roleToRemove, setRoleToRemove] = useState<{ id: string; name: string } | null>(null)

  const privileged = isOwner || isAdmin
  const targetIsPrivileged = !!effective && (effective.owner || effective.admin)
  const isSelf = currentUserId === userId
  // Mirrors the backend escalation guard; the API enforces it regardless.
  const canManagePermissions = can(PERMISSION_KEYS.USER_MANAGE_PERMISSIONS)
    && (privileged || (!isSelf && !targetIsPrivileged))
  const canManageRoles = (can(PERMISSION_KEYS.USER_MANAGE_ROLES) || canAnywhere(PERMISSION_KEYS.ROLE_MANAGE_USERS))
    && (privileged || (!isSelf && !targetIsPrivileged))
  const canGrantKey = (key: string) => privileged || can(key)

  /** Workspace-wide keys the user gets without an override (roles, admin, owner). */
  const inherited = useMemo(() => {
    const map = new Map<string, PermissionSource[]>()
    if (!effective || !index) return map
    const adminRole = effective.roles.find((r) => r.roleType === RoleType.ADMIN)
    for (const action of index.actionByKey.values()) {
      if (effective.owner) {
        map.set(action.key, [{ type: 'OWNER', grantId: null, roleId: null, roleName: null }])
      } else if (effective.admin && !action.billingSensitive) {
        map.set(action.key, [{ type: 'ADMIN', grantId: null, roleId: adminRole?.id ?? null, roleName: adminRole?.name ?? null }])
      }
    }
    for (const p of effective.permissions) {
      if (p.scope !== 'GLOBAL') continue
      const fromRoles = p.sources.filter((s) => s.type === 'ROLE')
      if (fromRoles.length > 0) map.set(p.permissionKey, [...(map.get(p.permissionKey) ?? []), ...fromRoles])
    }
    return map
  }, [effective, index])

  const overrideGlobalKeys = useMemo(
    () =>
      (effective?.permissions ?? [])
        .filter((p) => p.scope === 'GLOBAL' && p.sources.some((s) => s.type === 'OVERRIDE'))
        .map((p) => p.permissionKey),
    [effective]
  )

  useEffect(() => {
    setSelected(new Set(overrideGlobalKeys))
    setHasChanges(false)
  }, [overrideGlobalKeys])

  const scopedGrants: ScopedGrantRow[] = useMemo(
    () =>
      (effective?.permissions ?? [])
        .filter((p) => p.scope !== 'GLOBAL')
        .map((p) => ({
          rowKey: `${p.permissionKey}:${p.scope}:${p.resourceId}`,
          permissionKey: p.permissionKey,
          resourceType: p.resourceType,
          resourceId: p.resourceId,
          scope: p.scope,
          sources: p.sources,
          revokeGrantId: p.sources.find((s) => s.type === 'OVERRIDE')?.grantId ?? null,
        })),
    [effective]
  )
  const resolveName = useResourceNameResolver(scopedGrants)

  const refresh = () => {
    invalidatePermissionQueries(queryClient)
    queryClient.invalidateQueries({ queryKey: ['workspace-users'] })
  }

  /** Runs an override change; if the user already has the access through a role, asks first. */
  const withDuplicateCheck = async (attempt: (confirmDuplicate: boolean) => Promise<void>, onDone: () => void) => {
    try {
      await attempt(false)
      onDone()
    } catch (err) {
      const conflict = asPermissionAlreadyInherited(err)
      if (!conflict) {
        toast.error((err as { detail?: string })?.detail ?? 'Failed to update permissions')
        return
      }
      setPendingDuplicate({ conflict, retry: async () => { await attempt(true); onDone() } })
    }
  }

  const buildReplaceRequest = (): ReplacePermissionsRequest => ({
    permissions: [
      ...scopedGrants
        .filter((g) => g.revokeGrantId)
        .map((g) => ({ permissionKey: g.permissionKey, resourceType: g.resourceType, resourceId: g.resourceId, scope: g.scope })),
      ...Array.from(selected).map((key) => ({ permissionKey: key, scope: 'GLOBAL' as const, resourceId: null })),
    ],
  })

  const saveMutation = useMutation({
    mutationFn: () =>
      withDuplicateCheck(
        (confirmDuplicate) => replaceUserPermissions(userId!, buildReplaceRequest(), { confirmDuplicate }),
        () => {
          refresh()
          setHasChanges(false)
          toast.success('Permissions saved')
        }
      ),
  })

  const grantMutation = useMutation({
    mutationFn: (data: ScopedGrantInput) =>
      withDuplicateCheck(
        (confirmDuplicate) => grantUserPermission({ granteeUserId: userId!, ...data }, { confirmDuplicate }),
        () => {
          refresh()
          setAddCategory(null)
          toast.success('Access granted')
        }
      ),
  })

  const revokeMutation = useMutation({
    mutationFn: (row: ScopedGrantRow) => revokeUserGrant(userId!, row.revokeGrantId!),
    onSuccess: () => {
      refresh()
      toast.success('Override revoked')
    },
  })

  const addRoleMutation = useMutation({
    mutationFn: (roleId: string) => assignRole(userId!, roleId),
    onSuccess: () => {
      setRoleToAdd('')
      refresh()
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      toast.success('Role assigned')
    },
  })

  const removeRoleMutation = useMutation({
    mutationFn: (roleId: string) => removeRole(userId!, roleId),
    onSuccess: () => {
      setRoleToRemove(null)
      refresh()
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      toast.success('Role removed')
    },
  })

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
    setHasChanges(true)
  }

  const toggleMany = (keys: string[], value: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      keys.forEach((k) => (value ? next.add(k) : next.delete(k)))
      return next
    })
    setHasChanges(true)
  }

  /** Overrides that duplicate what the role grants: they keep working after the role is removed. */
  const overridesSurvivingRemoval = useMemo(() => {
    if (!roleToRemove || !effective) return []
    return effective.permissions.filter(
      (p) => p.sources.some((s) => s.type === 'ROLE' && s.roleId === roleToRemove.id)
        && p.sources.some((s) => s.type === 'OVERRIDE')
    )
  }, [roleToRemove, effective])

  const userRoles = effective?.roles ?? []
  const assignableRoles = allRoles.filter(
    (r) => r.active && !userRoles.some((ur) => ur.id === r.id)
      && (privileged || r.roleType === RoleType.USER)
  )

  if (usersLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <h2 className="text-lg font-medium">User not found</h2>
          <Button variant="link" onClick={() => navigate('/users')}>Back to Users</Button>
        </div>
      </div>
    )
  }

  const isLocked = lockedResourceIds.has(user.userId)

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/users')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Users
        </Button>

        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 select-none items-center justify-center rounded-full bg-primary-100 text-lg font-bold text-primary-700">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-2xl font-bold leading-none">{user.name}</h1>
                {effective?.owner && (
                  <Badge className="gap-1 bg-primary-600"><Crown className="h-3 w-3" /> Owner</Badge>
                )}
                {effective?.admin && !effective.owner && (
                  <Badge className="gap-1 bg-primary-600"><Shield className="h-3 w-3" /> Admin</Badge>
                )}
                {isLocked && (
                  <Badge variant="outline" className="gap-1 border-amber-300 text-amber-700">
                    <Lock className="h-3 w-3" /> Locked
                  </Badge>
                )}
              </div>
              <div className="mt-1 flex items-center gap-1 truncate text-sm text-neutral-500">
                <Mail className="h-3.5 w-3.5 shrink-0" />
                {user.email}
              </div>
            </div>
          </div>

          {canManagePermissions && !effective?.owner && (
            <Button onClick={() => saveMutation.mutate()} disabled={!hasChanges || saveMutation.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {saveMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          )}
        </div>
      </div>

      {targetIsPrivileged && (
        <div className="flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            {effective?.owner
              ? 'This user owns the workspace and has every permission.'
              : 'This user is an Admin and has every permission except billing. Overrides only matter for billing permissions or if the admin role is removed.'}
          </p>
        </div>
      )}

      {/* ── Roles ─────────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Roles</CardTitle>
          <CardDescription>Most access should come from roles; use overrides for exceptions.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {effectiveLoading ? (
            <Skeleton className="h-8 w-full" />
          ) : userRoles.length === 0 ? (
            <p className="text-sm text-neutral-500">No roles assigned.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {userRoles.map((role) => (
                <span
                  key={role.id}
                  className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 py-1 pl-3 pr-1 text-sm"
                >
                  <button type="button" className="hover:underline" onClick={() => navigate(`/roles/${role.id}`)}>
                    {role.name}
                  </button>
                  {canManageRoles && (privileged || role.roleType === RoleType.USER) ? (
                    <button
                      type="button"
                      aria-label={`Remove ${role.name}`}
                      className="rounded-full p-1 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                      onClick={() => setRoleToRemove({ id: role.id, name: role.name })}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <span className="w-2" />
                  )}
                </span>
              ))}
            </div>
          )}

          {canManageRoles && (
            <div className="flex flex-col gap-2 sm:flex-row">
              <select
                value={roleToAdd}
                onChange={(e) => setRoleToAdd(e.target.value)}
                className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm outline-none focus:border-primary-500 sm:max-w-xs"
                aria-label="Role to assign"
              >
                <option value="">Choose a role to assign…</option>
                {assignableRoles.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              <Button
                onClick={() => addRoleMutation.mutate(roleToAdd)}
                disabled={!roleToAdd || addRoleMutation.isPending}
                className="gap-1.5"
              >
                <Plus className="h-4 w-4" />
                Assign role
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Permissions ───────────────────────────────────────────────────── */}
      {!effective?.owner && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Permissions
            </CardTitle>
            <CardDescription>
              Badges show access the user already has through roles. Checkboxes are personal overrides that
              apply regardless of roles.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {effectiveLoading || !index ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : (
              <PermissionTabs
                index={index}
                selected={selected}
                inherited={inherited}
                onToggle={toggle}
                onToggleMany={toggleMany}
                scopedGrants={scopedGrants}
                resolveName={resolveName}
                onRevoke={(row) => revokeMutation.mutate(row)}
                onAddScoped={setAddCategory}
                canGrantKey={canGrantKey}
                readOnly={!canManagePermissions}
                revoking={revokeMutation.isPending}
              />
            )}
          </CardContent>
        </Card>
      )}

      {index && (
        <AddResourcePermissionDialog
          open={addCategory !== null}
          onOpenChange={(open) => !open && setAddCategory(null)}
          options={addCategory ? instanceGrantableActions(index, addCategory) : []}
          recordGroups={recordGroups}
          onSave={(data) => grantMutation.mutate(data)}
          isSaving={grantMutation.isPending}
        />
      )}

      <DuplicateOverrideDialog
        conflict={pendingDuplicate?.conflict ?? null}
        index={index}
        userName={user.name}
        pending={confirmingDuplicate}
        onCancel={() => setPendingDuplicate(null)}
        onConfirm={async () => {
          if (!pendingDuplicate) return
          setConfirmingDuplicate(true)
          try {
            await pendingDuplicate.retry()
            setPendingDuplicate(null)
          } catch (err) {
            toast.error((err as { detail?: string })?.detail ?? 'Failed to add override')
          } finally {
            setConfirmingDuplicate(false)
          }
        }}
      />

      <ConfirmDialog
        open={!!roleToRemove}
        onOpenChange={(open) => !open && setRoleToRemove(null)}
        title={`Remove the ${roleToRemove?.name ?? ''} role from ${user.name}?`}
        description="They lose every permission that comes only from this role."
        confirmLabel="Remove role"
        destructive
        pending={removeRoleMutation.isPending}
        onConfirm={() => roleToRemove && removeRoleMutation.mutate(roleToRemove.id)}
      >
        {overridesSurvivingRemoval.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <p className="font-medium">These personal overrides duplicate the role and will keep granting access:</p>
            <ul className="mt-1 list-disc pl-4">
              {overridesSurvivingRemoval.map((p) => (
                <li key={`${p.permissionKey}:${p.scope}:${p.resourceId}`}>
                  {index?.actionByKey.get(p.permissionKey)?.label ?? p.permissionKey}
                </li>
              ))}
            </ul>
            <p className="mt-1">Revoke them on the Permissions card if the access should end with the role.</p>
          </div>
        )}
      </ConfirmDialog>
    </div>
  )
}
